import React from 'react';
import {
  Archive,
  ArchiveRestore,
  Ban,
  Check,
  CircleCheck,
  CircleMinus,
  CloudUpload,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  FlaskConical,
  IdCard,
  Import,
  KeyRound,
  List,
  ListTree,
  Mail,
  Pencil,
  Plus,
  RotateCw,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  SquarePen,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import cn from './primitives/cn';

/**
 * The icon for a row action, drawn in the rail's outline set rather than Font Awesome.
 *
 * Every screen names its row actions' icons with a Font Awesome class string
 * (`fa-solid fa-pencil`), dozens of call sites, and some pick the class per row. Rather
 * than rewrite them, this reads the icon name out of the string and draws its lucide
 * counterpart, so the menu matches the sidebar and Ryan's Collections mockup (pencil,
 * upload, copy, trash). A name with no counterpart here still renders, as the Font Awesome
 * glyph it always was, so a new action never loses its icon.
 */
const OUTLINE = {
  'fa-pencil': Pencil,
  'fa-pen-field': SquarePen,
  'fa-trash': Trash2,
  'fa-trash-arrow-up': ArchiveRestore,
  'fa-box-archive': Archive,
  'fa-clone': Copy,
  'fa-upload': Upload,
  'fa-cloud-arrow-up': CloudUpload,
  'fa-download': Download,
  'fa-file-import': Import,
  'fa-arrow-rotate-right': RotateCw,
  'fa-list-tree': ListTree,
  'fa-list': List,
  'fa-users': Users,
  'fa-sliders': SlidersHorizontal,
  'fa-magnifying-glass': Search,
  'fa-search': Search,
  'fa-circle-check': CircleCheck,
  'fa-circle-minus': CircleMinus,
  'fa-check': Check,
  'fa-times': X,
  'fa-add': Plus,
  'fa-paper-plane': Send,
  'fa-envelope': Mail,
  'fa-key': KeyRound,
  'fa-gear': Settings,
  'fa-flask': FlaskConical,
  'fa-eye': Eye,
  'fa-eye-slash': EyeOff,
  'fa-id-card': IdCard,
  'fa-ban': Ban,
  // A typo for fa-file-pdf at its one call site, which Font Awesome itself cannot draw.
  'fa-solid-pdf': FileText,
};

export const outlineIconFor = (iconClass = '') =>
  iconClass
    .split(/\s+/)
    .map((name) => OUTLINE[name])
    .find(Boolean) || null;

const ActionIcon = ({ icon, className }) => {
  if (!icon) return null;
  const Outline = outlineIconFor(icon);
  if (Outline) {
    return (
      <Outline
        className={cn('tw-shrink-0', className)}
        size={16}
        strokeWidth={1.75}
        aria-hidden="true"
      />
    );
  }
  return (
    <i className={cn(icon, 'tw-w-4 tw-shrink-0 tw-text-center', className)} aria-hidden="true" />
  );
};

export default ActionIcon;
